import { useCallback, useMemo, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import '../../../shared/ui/ag-grid-theme.css'
import type {
  CellClassParams,
  ColDef,
  CellValueChangedEvent,
  ColumnResizedEvent,
  DragStoppedEvent,
  SortChangedEvent,
  SuppressKeyboardEventParams,
  ValueFormatterParams,
  ValueParserParams,
} from 'ag-grid-community'
import { VatMode, type OrderInvoiceDto, type UpdateInvoiceItemDto } from '@crm/shared'
import { updateInvoiceItem } from '../../../shared/api/orders.api'
import { toast } from '../../../shared/ui/toastStore'
import { CheckboxSetFilter } from '../../table/CheckboxSetFilter'
import { AG_GRID_LOCALE_RU } from '../../../shared/ag-grid/locale'
import { useGridSelection } from '../../../shared/hooks/useGridSelection'
import { useAuthStore } from '../../auth/authStore'
import { useOrderColState } from '../hooks/useOrderColState'
import { formatMoney, formatPrice, formatDate } from '../orderFormat'

ModuleRegistry.registerModules([AllCommunityModule])

type InvRow = {
  id: string
  partNumber: string
  unfNameResolved: string
  category: string | null
  brand: string | null
  quantity: number
  price: number
  sum: number
}

const moneyFmt = (p: ValueFormatterParams) =>
  p.value == null ? '' : formatMoney(p.value as number)

// Цена — полная, до 4 знаков: иначе 12,35 × 1000 шт на экране не сходится с
// суммой строки 12 345,60 (сумма считается от полной цены 12,3456).
const priceFmt = (p: ValueFormatterParams) =>
  p.value == null ? '' : formatPrice(p.value as number)

/** Источник события для отката ячейки: такое изменение не сохраняем. */
const ROLLBACK = 'rollback'

// Парсер введённого числа: срезает «₽», пробелы-разряды и принимает запятую
// как десятичный разделитель. Невалидный ввод → откат к прежнему значению.
// Стёртая ячейка — тоже откат: раньше пустая строка давала Number('') = 0,
// и стёртая цена сохранялась нулём (аудит 30.09.2026).
const numParser = (p: ValueParserParams): number => {
  const raw = String(p.newValue ?? '')
    .replace(/[^\d.,-]/g, '')
    .replace(',', '.')
  const n = raw === '' ? NaN : Number(raw)
  return Number.isFinite(n) ? n : (p.oldValue as number)
}

/**
 * Модалка позиций счёта. Редактируются прямо в ячейке:
 *  - «Наименование позиции» — то, что уйдёт в 1-й столбец шаблона УНФ (ручная
 *    правка приоритетнее справочника 1С; пустая ячейка = вернуть авто);
 *  - «Наименование» — сегмент партномера (Диод/Микросхема/Разъём…), пишется в
 *    позицию заявки и виден в «Позициях заказа»;
 *  - «Бренд» — синкается в снапшот счёта и живую позицию заявки;
 *  - количество и цена — пересчёт суммы счёта и статуса оплаты.
 */
export function InvoiceItemsModal({
  invoice,
  orderId,
  canMutate,
  canEditMoney,
  onClose,
}: {
  invoice: OrderInvoiceDto
  orderId: string
  canMutate: boolean
  /** Цена и количество — только пока заказ можно менять (до «Оплачен», isOrderEditable). */
  canEditMoney: boolean
  onClose: () => void
}) {
  const user = useAuthStore((s) => s.user)
  const qc = useQueryClient()
  const gridRef = useRef<AgGridReact>(null)
  const { onCellMouseDown, onCellMouseOver, getCellStyle } = useGridSelection(gridRef)
  // v2 — колонки «Наименование позиции» (УНФ) и «Наименование» (сегмент).
  const { saveState, onGridReady } = useOrderColState('crm_invoice_items_cols_v2', user?.id)

  // Правка позиции счёта: бэк пересчитывает сумму счёта/статус оплаты (для цены
  // и количества) и синкает бренд/сегмент в живую позицию заявки — «Позиции
  // заказа» обновятся после инвалидации заказа.
  const save = useMutation({
    mutationFn: ({ itemId, patch }: { itemId: string; patch: UpdateInvoiceItemDto }) =>
      updateInvoiceItem(itemId, patch),
    onSuccess: () => {
      toast.success('Позиция счёта обновлена')
      void qc.invalidateQueries({ queryKey: ['orders', orderId] })
    },
    onError: (e) => {
      // Серверная 422 (например, «сумма позиции больше допустимой») — дословно,
      // иначе сотрудник не поймёт, что именно исправить.
      const msg = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data
        ?.error?.message
      toast.error(msg ?? 'Не удалось обновить позицию счёта')
      void qc.invalidateQueries({ queryKey: ['orders', orderId] })
    },
  })

  // Стрелки ↑/↓ в режиме правки: коммит + переход на соседнюю ячейку (как в
  // таблице карточки заявки) — удобно вводить цифры подряд.
  const suppressKeyboardEvent = useCallback((params: SuppressKeyboardEventParams): boolean => {
    if (!params.editing) return false
    const key = (params.event as KeyboardEvent).key
    if (key !== 'ArrowDown' && key !== 'ArrowUp') return false
    params.api.stopEditing(false)
    const cur = params.node.rowIndex ?? 0
    const next = key === 'ArrowDown' ? cur + 1 : cur - 1
    if (next < 0 || next > params.api.getDisplayedRowCount() - 1) return true
    params.api.setFocusedCell(next, params.column)
    return true
  }, [])

  const rowData = useMemo<InvRow[]>(
    () =>
      invoice.items.map((it) => ({
        id: it.id,
        partNumber: it.partNumber,
        unfNameResolved: it.unfNameResolved,
        category: it.category,
        brand: it.brand,
        quantity: it.quantity,
        price: it.price,
        sum: it.sum,
      })),
    [invoice.items],
  )

  // Жёлтая подсветка правимых ячеек — inline-стилем поверх getCellStyle, как в
  // «Позициях заказа»: прозрачный фон выделения перебивал класс Tailwind.
  const editStyle = useCallback(
    (on: boolean) =>
      on
        ? {
            cellStyle: (p: CellClassParams) => {
              const st = getCellStyle(p)
              return st.background === 'transparent'
                ? { background: 'var(--crm-edit-cell-bg)' }
                : st
            },
          }
        : {},
    [getCellStyle],
  )
  const editableTextClass = canMutate ? 'cursor-text' : ''

  const colDefs = useMemo<ColDef<InvRow>[]>(
    () => [
      {
        colId: 'num',
        headerName: '№',
        width: 56,
        minWidth: 56,
        filter: false,
        sortable: false,
        resizable: false,
        suppressMovable: true,
        cellClass: 'text-gray-500',
        valueGetter: (p) => (p.node?.rowIndex ?? 0) + 1,
      },
      {
        field: 'unfNameResolved',
        headerName: 'Наименование позиции',
        minWidth: 240,
        flex: 2,
        wrapText: true,
        autoHeight: true,
        editable: canMutate,
        cellClass: editableTextClass,
        ...editStyle(canMutate),
        tooltipValueGetter: (p) => `Партномер: ${p.data?.partNumber ?? ''}`,
      },
      {
        field: 'category',
        headerName: 'Наименование',
        minWidth: 140,
        wrapText: true,
        autoHeight: true,
        editable: canMutate,
        cellClass: editableTextClass,
        ...editStyle(canMutate),
        valueFormatter: (p) => (p.value ? String(p.value) : '—'),
      },
      {
        field: 'brand',
        headerName: 'Бренд',
        minWidth: 130,
        wrapText: true,
        autoHeight: true,
        editable: canMutate,
        cellClass: editableTextClass,
        ...editStyle(canMutate),
        valueFormatter: (p) => (p.value ? String(p.value) : '—'),
      },
      {
        field: 'quantity',
        headerName: 'Кол-во',
        minWidth: 88,
        filter: false,
        editable: canEditMoney,
        cellClass: canEditMoney ? 'text-right cursor-text' : 'text-right',
        ...editStyle(canEditMoney),
        headerClass: 'ag-right-aligned-header',
        valueParser: numParser,
      },
      {
        field: 'price',
        headerName: 'Цена',
        minWidth: 120,
        filter: false,
        editable: canEditMoney,
        cellClass: canEditMoney ? 'text-right cursor-text' : 'text-right',
        ...editStyle(canEditMoney),
        headerClass: 'ag-right-aligned-header',
        valueFormatter: priceFmt,
        valueParser: numParser,
      },
      {
        field: 'sum',
        headerName: 'Сумма',
        minWidth: 130,
        filter: false,
        cellClass: 'text-right font-medium',
        headerClass: 'ag-right-aligned-header',
        valueFormatter: moneyFmt,
      },
    ],
    [canMutate, canEditMoney, editableTextClass, editStyle],
  )

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: false,
      filter: CheckboxSetFilter,
      floatingFilter: false,
      cellStyle: getCellStyle,
      suppressKeyboardEvent,
    }),
    [getCellStyle, suppressKeyboardEvent],
  )

  const onCellValueChanged = useCallback(
    (e: CellValueChangedEvent<InvRow>) => {
      const field = e.colDef.field
      // Откат своей же ячейки — не правка: иначе уходил второй PATCH и ложный тост.
      if (e.source === ROLLBACK) return
      if (e.newValue === e.oldValue) return
      // Числовые поля: цена ≥ 0, количество > 0; иначе откатываем ячейку.
      if (field === 'price' || field === 'quantity') {
        if (typeof e.newValue !== 'number') return
        if (field === 'price' && e.newValue < 0) {
          e.node.setDataValue('price', e.oldValue, ROLLBACK)
          return
        }
        if (field === 'quantity' && e.newValue <= 0) {
          e.node.setDataValue('quantity', e.oldValue, ROLLBACK)
          return
        }
        // Цена — до 4 знаков (точность цены позиции), не до копеек: сумму
        // строки сервер считает от полной цены.
        const patch: UpdateInvoiceItemDto =
          field === 'price'
            ? { price: Math.round(e.newValue * 10_000) / 10_000 }
            : { quantity: e.newValue }
        save.mutate({ itemId: e.data.id, patch })
        return
      }
      // Текстовые поля: пустая строка → null (для «Наименования позиции» это
      // сброс к авто-вычислению из справочника/категории).
      if (field === 'unfNameResolved' || field === 'category' || field === 'brand') {
        const text = String(e.newValue ?? '').trim() || null
        const patch: UpdateInvoiceItemDto =
          field === 'unfNameResolved' ? { unfName: text } : { [field]: text }
        save.mutate({ itemId: e.data.id, patch })
      }
    },
    [save],
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[85vh] w-full max-w-6xl overflow-hidden rounded-xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {invoice.invoiceNumber ? `Счёт № ${invoice.invoiceNumber}` : 'Счёт (без номера УНФ)'}
          </h3>
          <button className="text-gray-400 hover:text-gray-600" onClick={onClose}>
            ✕
          </button>
        </div>
        <p className="mb-1 text-sm text-gray-500">
          {formatMoney(invoice.totalSum)} · НДС {invoice.vatRate}%
          {invoice.vatMode === VatMode.ON_TOP
            ? ' сверху (в УНФ после загрузки шаблона переключите заказ на «НДС сверху»)'
            : ''}{' '}
          · {formatDate(invoice.createdAt)} · колонки можно тянуть и менять местами, ячейки —
          выделять и копировать
        </p>
        {canMutate && (
          <p className="mb-2 text-xs text-amber-600">
            Правка в ячейке (двойной клик или Enter): «Наименование позиции» уходит в шаблон УНФ
            (пустая ячейка — вернуть авто), «Наименование» (сегмент) и «Бренд» обновляются и в
            позициях заказа/заявки
            {canEditMoney
              ? ', цена и количество пересчитывают сумму счёта.'
              : '. Цена и количество не меняются: заказ оплачен или закрыт.'}
          </p>
        )}
        <div className="crm-table ag-theme-alpine h-[60vh] overflow-hidden rounded-lg border border-gray-200 bg-white">
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            localeText={AG_GRID_LOCALE_RU}
            getRowId={(p) => p.data.id}
            onGridReady={onGridReady}
            onCellMouseDown={onCellMouseDown}
            onCellMouseOver={onCellMouseOver}
            onDragStopped={(e: DragStoppedEvent) => saveState(e.api)}
            onColumnResized={(e: ColumnResizedEvent) => {
              if (e.finished) saveState(e.api)
            }}
            onSortChanged={(e: SortChangedEvent) => saveState(e.api)}
            onCellValueChanged={onCellValueChanged}
            rowHeight={40}
            headerHeight={40}
            suppressDragLeaveHidesColumns
            stopEditingWhenCellsLoseFocus
          />
        </div>
        <div className="mt-4 text-right">
          <button
            className="rounded-lg bg-gray-100 px-4 py-1.5 text-sm hover:bg-gray-200"
            onClick={onClose}
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  )
}
